import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { CircularProgress, IconButton, iconButtonClasses } from '@mui/material';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import {
  useCaseListContext,
  UseCaseArray,
} from '../../../../shared/context/CaseListContext';
import calendarMinus from '../../assets/icons/icon_calendar-minus.svg';
import { BetweenLayout } from '../../common/components/BetweenLayout';
import { ColoredIcon } from '../../common/components/Icon';
import { Spacer } from '../../common/components/Spacer';
import {
  SectionContentContainer,
  SectionHeading,
} from '../../common/components/styledComponents';
import { CurrentTimeText } from '../../common/components/styledComponents';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

import { PreviewCases } from './PreviewCases';
import { PreviewMobileCases } from './PreviewMobileCases';
import { ResetSessionIdContext } from './SessionIdContext';

const TopContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin: 0;
`;

export const TimerText = styled.div`
  display: flex;
  align-self: center;
  font-size: 16px;
  font-weight: 700;
  color: ${p => 'var(--HScan_Light_Blue, #2e5be0)' ?? p.theme.colors.black2};
`;
const TitleText = styled(TimerText)`
  margin-right: 5px;
  word-break: keep-all;
`;
const SaveButtonSxProps = {
  [`&.${iconButtonClasses.root}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    width: '100%',
    height: '30px',
    marginTop: '10px',
    boxSizing: 'borderBox',
    border: 'none',
    borderRadius: 0,
    backgroundColor: '#2e5be0',
    color: '#ffffff',
    lineHeight: '21px',
    fontSize: '14px',
    fontWeight: 600,
    whitespace: 'nowrap',
    cursor: 'pointer',

    [`&.${iconButtonClasses.disabled}`]: {
      backgroundColor: '#e8e8e8',
      color: '#bdbdbd',
      cursor: 'notAllowed',
    },
  },
};
interface TopContentsProps {
  sessionEndAt: string;
  linkEndAt: string;
  onSave?: () => void;
  isSaving?: boolean;
  disableSaveButton?: boolean;
}
export const TopContents = ({
  sessionEndAt,
  linkEndAt,
  onSave,
  isSaving,
  disableSaveButton,
}: TopContentsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.shareToPatientImageList',
  });

  const [dDay, setDDay] = useState<number | string>('');

  useEffect(() => {
    const timeDiffwithCurrent = (date: string) => {
      const diffDate = new Date(date);
      const nowDate = new Date();
      return Math.ceil(
        Math.abs(diffDate.getTime() - nowDate.getTime()) /
          (1000 * 60 * 60 * 24),
      );
    };
    setDDay(timeDiffwithCurrent(linkEndAt));
  }, [linkEndAt, sessionEndAt]);

  return (
    <>
      <TopContainer>
        <TitleWrapper>
          <TitleText>{t('title')}</TitleText>
          <ColoredIcon
            width="24px"
            height="24px"
            imageUrl={calendarMinus}
            imageWidth="20px"
            color={'#2e5be0'}
          />
          <TimerText>
            {`${t('dDay.text', {
              days: dDay ?? 0,
            })}`}
          </TimerText>
        </TitleWrapper>
        <IconButton
          sx={SaveButtonSxProps}
          onClick={onSave}
          disabled={disableSaveButton}>
          {isSaving ? (
            <CircularProgress size={16} style={{ color: 'white' }} />
          ) : (
            <>
              <SaveAltIcon sx={{ marginRight: '5px' }} />
              {t('saveButton')}
            </>
          )}
        </IconButton>
      </TopContainer>
    </>
  );
};

export interface PreviewImageListProps {
  sessionEndAt: string;
  linkEndAt: string;
  onSave?: () => void;
  isSaving?: boolean;
  disableSaveButton?: boolean;
}
export const PreviewImageList = ({
  sessionEndAt,
  linkEndAt,
  onSave,
  isSaving,
  disableSaveButton,
}: PreviewImageListProps) => {
  const resetSessionId = useContext(ResetSessionIdContext);
  const { t: globalT } = useTranslation('translation');
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.shareToPatientImageList',
  });
  const windowSize = useContext(WindowSizeContext);

  const caseListContext = useCaseListContext();
  const useCase = caseListContext.useCase as UseCaseArray;

  const { data: cases = [], isFetching: isFetchingCases, isError } = useCase();
  useEffect(() => {
    if (isError) {
      resetSessionId();
    }
  }, [isError, resetSessionId]);

  const currentTime = moment().format(`${globalT('currentTimeWithoutLabel')}`);

  return (
    <SectionContentContainer>
      <TopContents
        sessionEndAt={sessionEndAt}
        linkEndAt={linkEndAt}
        onSave={onSave}
        isSaving={isSaving}
        disableSaveButton={disableSaveButton}
      />
      <Spacer height={20} />
      <BetweenLayout
        left={<SectionHeading>{t('heading')}</SectionHeading>}
        right={
          <CurrentTimeText>
            {t('currentTime')}
            <Spacer width={5} />
            {currentTime}
          </CurrentTimeText>
        }
      />
      {!!cases.length && windowSize === WindowSize.MOBILE ? (
        <>
          <Spacer height="20px" />
          <PreviewMobileCases
            data={cases}
            dataToShow={cases}
            page={0}
            totalCount={cases.length}
            isLoading={isFetchingCases}
          />
        </>
      ) : (
        <PreviewCases cases={cases} />
      )}
    </SectionContentContainer>
  );
};
